import React, { useState, useEffect } from 'react';
import RTModal from '../common/RTModal';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import { apiPost } from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import './AddEBChargeModal.css';

const formatAmount = (amount) => (
  `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
);

const DetailRow = ({ label, value }) => (
  <div className="eb-charge-modal-row">
    <span className="eb-charge-modal-label">{label}:</span>
    <span className="eb-charge-modal-value">{value || 'N/A'}</span>
  </div>
);

const AddEBChargeModal = ({ isOpen, invoice, onClose, onSaved }) => {
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setAmount('');
      setError('');
    }
  }, [isOpen, invoice]);

  if (!invoice) return null;

  const invoiceItems = invoice.invoice_items || [];
  const oldAmount = parseFloat(invoice.current_amount || 0);
  const ebVal = parseFloat(amount);
  const newAmount = !isNaN(ebVal) && ebVal > 0 ? oldAmount + ebVal : oldAmount;

  const handleSave = async () => {
    const val = parseFloat(amount);
    if (!amount || isNaN(val) || val <= 0) {
      setError('Enter a valid EB charge amount');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const response = await apiPost('/invoicing/eb-bill/add', {
        invoice_id: invoice.invoice_id,
        amount: val,
      });
      if (response.data?.success) {
        toastSuccess('EB charge added successfully');
        onSaved?.();
        onClose();
      } else {
        toastError('Failed to add EB charge');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to add EB charge';
      setError(typeof msg === 'string' ? msg : 'Failed to add EB charge');
      toastError(typeof msg === 'string' ? msg : 'Failed to add EB charge');
    } finally {
      setSaving(false);
    }
  };

  return (
    <RTModal
      isOpen={isOpen}
      onClose={saving ? undefined : onClose}
      title="Add EB Charge"
      size="medium"
      disableBackdropClick={saving}
      footer={(
        <>
          <RTButton variant="outline-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </RTButton>
          <RTButton variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </RTButton>
        </>
      )}
    >
      <div className="eb-modal-body-wrap">
        {saving && (
          <div className="eb-modal-saving-overlay">
            <RTSpinner message="Saving EB charge..." size={50} />
          </div>
        )}
        <div className="eb-charge-modal">
          <DetailRow label="Invoice" value={invoice.invoice_number} />
          <DetailRow label="PG" value={invoice.pg_name} />
          <DetailRow label="Mobile" value={invoice.mobile} />
          <DetailRow
            label="Building / Room"
            value={`${invoice.building_name || 'N/A'} — ${invoice.room_name || 'N/A'}`}
          />

          {invoiceItems.length > 0 && (
            <div className="eb-charge-modal-section">
              <h4 className="eb-charge-modal-section-title">Invoice Items</h4>
              <ul className="eb-charge-modal-items">
                {invoiceItems.map((item) => (
                  <li key={item.invoice_item_id} className="eb-charge-modal-item">
                    <span className="eb-charge-modal-item-label">
                      {item.label || item.invoice_item_type}
                    </span>
                    <span className="eb-charge-modal-item-amount">
                      {formatAmount(item.total_amount)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="eb-charge-modal-field">
            <label htmlFor="ebAmount">EB Charge Amount (₹)</label>
            <input
              id="ebAmount"
              type="number"
              min="0"
              step="0.01"
              className="rt-input"
              value={amount}
              onChange={(e) => { setAmount(e.target.value); setError(''); }}
              placeholder="Enter EB charge"
              disabled={saving}
            />
            {error && <div className="eb-charge-modal-error">{error}</div>}
          </div>

          <div className="eb-charge-modal-amounts">
            <div className="eb-charge-modal-amount-row">
              <span>Old invoice amount</span>
              <strong>{formatAmount(oldAmount)}</strong>
            </div>
            <div className="eb-charge-modal-amount-row eb-charge-modal-amount-row--new">
              <span>New invoice amount</span>
              <strong>{formatAmount(newAmount)}</strong>
            </div>
          </div>
        </div>
      </div>
    </RTModal>
  );
};

export default AddEBChargeModal;
